import {isEqual} from 'lodash';
import React, {memo, useState, useRef, useEffect, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Subject} from 'rxjs';
import {currentResource} from '../../util/utils';
import Utility from '../../util/utilityfunctions';
import {reviewNotesType, entities, reviewNoteStatus, rnhEngagementSources, entityTypes} from '../../util/uiconstants';
import {cleanSelectedNotes, getNoteDetail} from '../../actions/NotesModal/rnhnotedetailaction';
import {setIsModalOpen} from '../../actions/setismodalopen';
import {setIsMediaPlaying} from '../../actions/MediaPlaying/mediaPlayingAction';
import {clearErrors} from '../../actions/erroractions';
import NotesModalWrapper from './NotesModalWrapper';
import NotesHeader from './ReviewNotes/Header';

const modalSubject = new Subject({
	event: null
});

const modalObservable = modalSubject.asObservable();

const modalResponseSubject = new Subject({
	event: null
});

const modalResponse = modalResponseSubject.asObservable();

function ReactiveReviewModal({entityUId}) {
	const [showModal, setShowModal] = useState(false);
	const [modalProps, setModalProps] = useState({});
	const [externalLoading, setExternalLoading] = useState(false);

	const dispatch = useDispatch();
	const userPreferences = useSelector((state) => state.userPreferences);

	const subscriptionRef = useRef(null);

	useEffect(() => {
		subscriptionRef.current = modalObservable.subscribe({
			next: (args) => {
				setShowModal(true);
				setModalProps(args);
				dispatch(cleanSelectedNotes(true));
			}
		});

		return () => {
			if (subscriptionRef.current !== null) {
				subscriptionRef.current.unsubscribe();
			}
		};
	}, []);

	const onHide = useCallback((event) => {
		modalResponseSubject.next({type: 'close', event});
		modalSubject.next({type: 'close', event});
		setShowModal(false);
		setModalProps({});
	}, []);

	const renderModal = () => {
		const {
			engagementId,
			commentId,
			disableAdd,
			disableFilterControls,
			document,
			entityId,
			parentName,
			reviewnoteType,
			viewKey,
			entityUId
		} = modalProps;

		const isTask = () => reviewnoteType === reviewNotesType.task;

		const getAllDetails = () => {
			let requestsUrl = Utility.getAnnotationsUrl(
				commentId,
				document?.id || document?.value || entityId,
				engagementId,
				isTask() ? entities.Task : entities.Document,
				true,
				true,
				true,
				[reviewNoteStatus.Open.id, reviewNoteStatus.Cleared.id, reviewNoteStatus.Closed.id],
				null,
				null,
				null,
				null,
				1000,
				null,
				isTask() ? rnhEngagementSources.currentEngagement : rnhEngagementSources.allEngagements
			);

			dispatch(
				getNoteDetail(
					requestsUrl,
					false,
					false,
					{isModalRequest: true, engagementId: parseInt(engagementId), currentUserId: userPreferences.id},
					entityId
				)
			).then(() => setExternalLoading(false));
		};

		const renderDetails = () => {
			const props = {
				entityType: entityTypes.Document,
				parentName,
				document,
				engagementId,
				setIsLoading: setExternalLoading
			};

			return <NotesHeader {...props} />;
		};

		const props = {
			show: showModal,
			onClose: () =>
				dispatch(clearErrors())
					.then(() => {
						dispatch(setIsMediaPlaying(false));
						dispatch(setIsModalOpen(false));
					})
					.then(() => onHide()),
			entityId: document?.id,
			renderDetails,
			externalLoading,
			instructionalText: currentResource?.reviewNoteModalLabels?.notesModalInstructionalText.replace(
				'{0}',
				currentResource.reviewNoteModalLabels.documentLabel.toLowerCase()
			),
			handleGetNotes: getAllDetails,
			headerLabel: currentResource?.reviewNoteModalLabels?.documentReviewNotesLabel,
			disableAdd,
			disableFilters: disableFilterControls,
			viewKey: viewKey,
			bodyId: entityUId
		};

		return <NotesModalWrapper {...props} />;
	};

	return Object.keys(modalProps).length > 0 && modalProps.entityUId === entityUId && renderModal();
}

function toggleModal(props) {
	modalSubject.next(props);
}

export {modalResponse, toggleModal};

export default memo(ReactiveReviewModal, isEqual);
